import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useFeature } from '../../contexts/FeatureContext';
import { GoogleDriveAuth } from '../ui/GoogleDriveAuth';
import { SafeComponent } from '../ui/SafeComponent';
import { useSettingsStore } from '../../stores/settings';
import packageJson from '../../../package.json';

interface SidebarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export function Sidebar({ isCollapsed, onToggleCollapse }: SidebarProps) {
  const compactMode = useSettingsStore((s) => s.compactMode);
  const isCharacterSheetEnabled = useFeature('CHARACTER_SHEET');
  const isCombatEnabled = useFeature('COMBAT_PAGE');
  const isAdvancementEnabled = useFeature('ADVANCEMENT_PAGE');
  const isSettingsEnabled = useFeature('SETTINGS_PAGE');
  const isShopEnabled = useFeature('SHOP_PAGE');
  const isGmEnabled = useFeature('GM_PAGE');
  const isGoogleDriveEnabled = useFeature('GOOGLE_DRIVE');
  const areGoogleDriveTestsEnabled = useFeature('GOOGLE_DRIVE_TESTS');

  const mainNavItems = [
    { path: '/', label: 'Welcome', icon: '🏠', enabled: true },
    {
      path: '/character',
      label: 'Character Sheet',
      icon: '📋',
      enabled: isCharacterSheetEnabled
    },
    { path: '/combat', label: 'Combat', icon: '⚔️', enabled: isCombatEnabled },
    { path: '/advancement', label: 'Advancement', icon: '📈', enabled: isAdvancementEnabled },
    { path: '/shop', label: 'Equipment Shop', icon: '🛒', enabled: isShopEnabled },
    { path: '/gm', label: 'GM Tracker', icon: '💀', enabled: isGmEnabled },
    { path: '/rules', label: 'Rules Reference', icon: '📖', enabled: true },
    { path: '/house-rules', label: 'House Rules', icon: '📜', enabled: true },
    { path: '/settings', label: 'Settings', icon: '⚙️', enabled: isSettingsEnabled },
    { path: '/skills', label: 'Skills & Talents', icon: '🎯', enabled: true },
    { path: '/dice', label: 'Dice (Coming Soon)', icon: '🎲', enabled: true }
  ].filter((item) => item.enabled);

  const testNavItems = [
    { path: '/test/debug', label: 'Debug Settings', icon: '🔍', enabled: true },
    { path: '/test/theme', label: 'Theme Test', icon: '🎨', enabled: true },
    { path: '/test/basic', label: 'Basic Test', icon: '✅', enabled: true },
    { path: '/test/simple', label: 'Simple Test', icon: '🔧', enabled: true },
    { path: '/test/rulebook-settings', label: 'Rulebook Settings', icon: '📚', enabled: true },
    { path: '/test/shop', label: 'Shop Test', icon: '🛒', enabled: true },
    {
      path: '/test/google-drive-real',
      label: 'Google Drive Test',
      icon: '🔗',
      enabled: areGoogleDriveTestsEnabled
    }
  ].filter((item) => item.enabled);

  return (
    <div
      className={`${isCollapsed ? 'w-16' : compactMode ? 'w-52' : 'w-64'} bg-surface text-ink shadow-lg h-screen transition-all duration-300 flex-shrink-0 z-20 flex flex-col`}
    >
      {/* Header */}
      <div className={`${compactMode ? 'p-2' : 'p-4'} border-b border-line`}>
        <div className="flex items-center justify-between">
          {!isCollapsed && (
            <div>
              <h1 className={`${compactMode ? 'text-lg' : 'text-xl'} font-bold text-ink-strong`}>
                🚀 Rogue Trader
              </h1>
              <p className="text-sm text-ink-dim">Character Sheet</p>
            </div>
          )}
          <button
            onClick={onToggleCollapse}
            className="p-1 rounded text-ink-dim hover:bg-surface-hover hover:text-ink transition-colors"
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <span className="text-lg">{isCollapsed ? '▶' : '◀'}</span>
          </button>
        </div>
      </div>

      {/* Main Navigation */}
      <nav
        className={`${isCollapsed ? 'p-2' : compactMode ? 'p-2' : 'p-4'} flex-1 overflow-y-auto`}
      >
        <div className={`${compactMode ? 'space-y-0.5' : 'space-y-2'}`}>
          {mainNavItems.map((item) => (
            <NavLink key={item.path} {...item} isCollapsed={isCollapsed} />
          ))}
        </div>

        {/* Google Drive Section */}
        {isGoogleDriveEnabled && !isCollapsed && (
          <div className="mt-6 pt-6 border-t border-line">
            <h3 className="text-sm font-semibold text-ink-faint uppercase tracking-wide mb-3">
              Google Drive
            </h3>
            <div className="px-3">
              <SafeComponent componentName="Google Drive Auth">
                <GoogleDriveAuth onAuthChange={() => {}} />
              </SafeComponent>
            </div>
          </div>
        )}

        {/* Test Pages Section */}
        {!isCollapsed && <TestSection testNavItems={testNavItems} />}
      </nav>

      {!isCollapsed && (
        <div
          className={`${compactMode ? 'px-2 pt-2 pb-1' : 'px-4 pt-3 pb-2'} border-t border-line`}
        >
          <ThemeToggle />
        </div>
      )}
      {isCollapsed && (
        <div className="px-1 pt-2 pb-1 border-t border-line flex justify-center">
          <ThemeToggle compact />
        </div>
      )}

      {!isCollapsed && (
        <div
          className={`${compactMode ? 'px-2 py-2 text-[11px]' : 'px-4 py-3 text-xs'} border-t border-line text-ink-faint`}
        >
          Version {packageJson.version}
        </div>
      )}
    </div>
  );
}

/**
 * Theme cycle button: light → dark → system → light.
 * Reads/writes via the existing useSettingsStore.setTheme action which
 * updates the `dark` class on <html>.
 */
function ThemeToggle({ compact = false }: { compact?: boolean }) {
  const theme = useSettingsStore((s) => s.theme);
  const setTheme = useSettingsStore((s) => s.setTheme);

  const next: Record<typeof theme, typeof theme> = {
    light: 'dark',
    dark: 'system',
    system: 'light'
  };

  const meta: Record<typeof theme, { icon: string; label: string }> = {
    light: { icon: '☀', label: 'Light' },
    dark: { icon: '☾', label: 'Dark' },
    system: { icon: '🖥', label: 'System' }
  };

  const cur = meta[theme];

  if (compact) {
    return (
      <button
        onClick={() => setTheme(next[theme])}
        title={`Theme: ${cur.label} — click to cycle`}
        aria-label={`Theme: ${cur.label}. Click to cycle.`}
        className="w-9 h-9 flex items-center justify-center rounded text-ink-dim hover:bg-surface-hover hover:text-ink transition-colors text-base"
      >
        <span aria-hidden="true">{cur.icon}</span>
      </button>
    );
  }

  return (
    <button
      onClick={() => setTheme(next[theme])}
      title="Click to cycle theme"
      aria-label={`Theme: ${cur.label}. Click to cycle through Light, Dark, System.`}
      className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded text-xs text-ink-dim hover:bg-surface-hover hover:text-ink transition-colors"
    >
      <span className="flex items-center gap-2">
        <span aria-hidden="true" className="text-base">
          {cur.icon}
        </span>
        <span className="uppercase tracking-wider text-[10px] text-ink-faint">Theme</span>
        <span className="font-medium">{cur.label}</span>
      </span>
      <span aria-hidden="true" className="text-ink-faint">
        ↻
      </span>
    </button>
  );
}

// Navigation components
function NavLink({
  path,
  label,
  icon,
  isCollapsed
}: {
  path: string;
  label: string;
  icon: string;
  isCollapsed?: boolean;
}) {
  const location = useLocation();
  const isActive = location.pathname === path;
  const compactMode = useSettingsStore((s) => s.compactMode);

  return (
    <Link
      to={path}
      className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} px-3 ${compactMode ? 'py-1.5 text-sm' : 'py-2'} rounded-lg transition-colors ${
        isActive ? 'bg-nav-active text-ink-nav-active' : 'text-ink hover:bg-surface-hover'
      }`}
      title={isCollapsed ? label : undefined}
    >
      <span className="text-lg">{icon}</span>
      {!isCollapsed && <span className="font-medium">{label}</span>}
    </Link>
  );
}

function TestSection({
  testNavItems
}: {
  testNavItems: Array<{ path: string; label: string; icon: string }>;
}) {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div className="mt-6 pt-6 border-t border-line">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-3 py-2 text-sm font-semibold text-ink-faint uppercase tracking-wide hover:text-ink-dim transition-colors"
      >
        <span>Test Pages</span>
        <span className={`transform transition-transform ${isOpen ? 'rotate-90' : ''}`}>▶</span>
      </button>

      {isOpen && (
        <div className="mt-2 space-y-1">
          {testNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors text-ink-dim hover:bg-surface-hover hover:text-ink"
            >
              <span>{item.icon}</span>
              <span>{item.label}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
